import { IntentDataWithTopics } from './intentDataTypes';
import partition from 'lodash/fp/partition';
import {
  apiWorkflowTypes,
  slackWorkflowTypes,
  widgetWorkflowTypes,
} from 'src/pages/workflow-builder-edit/constants';
import {
  BuilderView,
  WorkflowTypes,
} from 'src/pages/workflow-builder-edit/types';
import { getOptionalWorkflowId } from 'src/pages/workflow-builder-edit/utils/getOptionalWorkflowId';
import { ABTestVariantInfo } from 'src/services/insights/types';
import {
  ConversationChannel,
  IntentData,
  TranslationChannel,
} from 'src/types/workflowBuilderAPITypes';
import { CommonIntentWorkflowType } from 'src/utils/enums';
import { isIntentActiveForChannel } from 'src/utils/solve/intentUtils';

export class IntentTableDataFilter<
  IntentDataType extends IntentDataWithTopics | IntentData,
> {
  constructor(private data: IntentDataType[]) {}

  // Filter by search substrings
  filterByKeyword = (searchText: string) => {
    if (searchText.trim()) {
      this.data = this.data.filter(({ intent_name: name }) => {
        return name.toLowerCase().includes(searchText.trim().toLowerCase());
      });
    }

    return this;
  };

  setIsOtherQuestionsActive = (channel: ConversationChannel) => {
    const knowledgeArticleIntentData = this.getKnowledgeArticle();
    const otherQuestionsIntentData = this.getOtherQuestions();

    if (
      !knowledgeArticleIntentData ||
      !otherQuestionsIntentData ||
      channel === 'email'
    ) {
      return this;
    }

    const isKnowledgeArticleActive = isIntentActiveForChannel(
      knowledgeArticleIntentData.active_workflow_types,
      channel,
    );

    if (isKnowledgeArticleActive) {
      this.setActiveWorkflowTypeOnCondition(
        datum =>
          datum.intent_definition_id ===
          CommonIntentWorkflowType.GENERAL_HANDOFF,
      );
    }

    return this;
  };

  setDefaultHandoffWorkflowActive = (
    channel: TranslationChannel = 'widget',
  ) => {
    const knowledgeArticleIntentData = this.getKnowledgeArticle();
    const otherQuestionsIntentData = this.getOtherQuestions();

    if (!knowledgeArticleIntentData) {
      // it should always exist, this is a to make typescript happy
      return this;
    }

    const isKnowledgeArticleActive = isIntentActiveForChannel(
      knowledgeArticleIntentData.active_workflow_types,
      channel,
    );

    // Show as active if Knowledge Retrieval is active AND Other Questions exists
    if (isKnowledgeArticleActive && otherQuestionsIntentData) {
      this.setActiveWorkflowTypeOnCondition(
        datum => getIsDefaultByChannel(channel, datum),
        [...widgetWorkflowTypes, ...apiWorkflowTypes, ...slackWorkflowTypes],
      );
      // Show as active if Other Questions does not exist, default handoff will be active regardless of knowledge retrieval
    } else if (!otherQuestionsIntentData) {
      this.setActiveWorkflowTypeOnCondition(
        datum => getIsDefaultByChannel(channel, datum),
        [...widgetWorkflowTypes, ...apiWorkflowTypes, ...slackWorkflowTypes],
      );
    }

    return this;
  };

  private getOtherQuestions = () => {
    return this.data.find(
      ({ intent_definition_id: intentId }) =>
        intentId === CommonIntentWorkflowType.GENERAL_HANDOFF,
    );
  };

  private getKnowledgeArticle = () => {
    return this.data.find(
      ({ intent_definition_id: intentId }) =>
        intentId === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE,
    );
  };

  private setActiveWorkflowTypeOnCondition = (
    conditionChecker: (datum: (typeof this.data)[number]) => boolean,
    activeWorkflowTypes: WorkflowTypes[] = [],
  ) => {
    this.data = this.data.map(datum => {
      if (conditionChecker(datum)) {
        return {
          ...datum,
          active_workflow_types: activeWorkflowTypes,
        };
      }

      return datum;
    });
  };

  getTableData = () => {
    return this.data;
  };
}

export const filterCommonIntentWorkflowsToTop = <
  IntentDataType extends IntentDataWithTopics | IntentData,
>(
  table: IntentDataType[],
) => {
  const [workflowsAtTop, otherWorkflows] = partition(
    ({ intent_definition_id }) =>
      intent_definition_id === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE ||
      intent_definition_id === CommonIntentWorkflowType.FALLBACK,
    table,
  );

  const [knowledgeArticle, fallback] = partition(
    ({ intent_definition_id }) =>
      intent_definition_id === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE,
    workflowsAtTop,
  );

  return [...knowledgeArticle, ...fallback, ...otherWorkflows];
};

export const getIsDefaultByChannel = (
  channel: TranslationChannel,
  { is_default_handoff: isDefaultHandoff = false }: Partial<IntentData>,
) => {
  return isDefaultHandoff;
};

export const getWorkflowIdByChannel = (
  channel: TranslationChannel,
  {
    api_workflow_id = '',
    intent_workflow_id = '',
    interactive_email_workflow_id = '',
    playbook_workflow_id = '',
    slack_workflow_id = '',
    voice_workflow_id = '',
    web_call_workflow_id = '',
  }: Partial<IntentData>,
) => {
  if (channel === 'widget') {
    return intent_workflow_id;
  }

  if (channel === 'slack') {
    return slack_workflow_id;
  }

  if (channel === 'api') {
    return api_workflow_id;
  }

  if (channel === 'interactive_email') {
    return interactive_email_workflow_id;
  }

  if (channel === 'voice') {
    return voice_workflow_id || intent_workflow_id;
  }

  if (channel === 'playbook') {
    return playbook_workflow_id || intent_workflow_id;
  }

  if (channel === 'web_call') {
    return web_call_workflow_id || intent_workflow_id;
  }

  return intent_workflow_id;
};

export const getAbTestVariantsForChannel = (
  variantsByWorkflowId: Map<string, ABTestVariantInfo[]>,
  channel: TranslationChannel,
  intent: Partial<IntentData>,
): ABTestVariantInfo[] | undefined => {
  const workflowId =
    getWorkflowIdByChannel(channel, intent) || intent.intent_workflow_id;
  return workflowId ? variantsByWorkflowId.get(workflowId) : undefined;
};

export const getWorkflowIdByAnyChannel = ({
  api_workflow_id = '',
  intent_workflow_id = '',
  interactive_email_workflow_id = '',
  playbook_workflow_id = '',
  slack_workflow_id = '',
  voice_workflow_id = '',
  web_call_workflow_id = '',
}: Partial<IntentData>) => {
  const allChannels = [
    'widget',
    'email',
    'interactive_email',
    'api',
    'slack',
    'voice',
    'playbook',
    'web_call',
  ] as const satisfies TranslationChannel[];

  return (
    allChannels
      .map(channel => {
        if (channel === 'widget') {
          return intent_workflow_id;
        }

        if (channel === 'slack') {
          return slack_workflow_id;
        }

        if (channel === 'api') {
          return api_workflow_id;
        }

        if (channel === 'interactive_email') {
          return interactive_email_workflow_id;
        }

        if (channel === 'voice') {
          return voice_workflow_id;
        }

        if (channel === 'playbook') {
          return playbook_workflow_id;
        }

        if (channel === 'web_call') {
          return web_call_workflow_id;
        }

        return intent_workflow_id;
      })
      .filter(Boolean)[0] || intent_workflow_id
  );
};

export const getChannelByWorkflowId = (
  intentsByProduct: Record<ConversationChannel, IntentData[]>,
  workflowId: string,
) => {
  for (const [channel, intents] of Object.entries(intentsByProduct) as [
    ConversationChannel,
    IntentData[],
  ][]) {
    if (
      intents.some(
        intent => getWorkflowIdByChannel(channel, intent) === workflowId,
      )
    ) {
      return channel;
    }
  }

  return 'widget';
};

export const getWorkflowId = (
  view: BuilderView,
  workflowsForIntent: {
    api_workflow_id: string;
    has_live_interactive_email_component: boolean;
    interactive_email_workflow_id: string | null;
    playbook_workflow_id: string | null;
    slack_workflow_id: string | null;
    voice_workflow_id: string | null;
    web_call_workflow_id?: string | null;
    widget_workflow_id: string;
  },
): string => {
  const workflowId = getOptionalWorkflowId(view, workflowsForIntent);

  if (!workflowId) {
    throw new Error(`${view} workflow_id not found for intent`);
  }

  return workflowId;
};

export const getWorkflowType = (
  {
    api_is_default_handoff,
    api_workflow_is_autoflow: isApiAutoflow,
    intent_definition_id: intentDefinitionId,
    is_autoflow: isAutoflow,
    is_default_handoff,
    is_handoff: isHandoff,
    slack_is_default_handoff,
    slack_workflow_is_autoflow: isSlackAutoflow,
    subflow_type,
  }: IntentDataWithTopics,
  channel: ConversationChannel,
  isFallbackWorkflowEnabled: boolean | undefined,
  isVoiceOutboundEnabled?: boolean,
  isClassicWorkflowsDeprecated?: boolean,
) => {
  const isKnowledge =
    intentDefinitionId === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE;
  const isFallback = intentDefinitionId === CommonIntentWorkflowType.FALLBACK;

  if (
    isFallback ||
    (getIsDefaultByChannel(channel, {
      api_is_default_handoff,
      is_default_handoff,
      slack_is_default_handoff,
    }) &&
      !isFallbackWorkflowEnabled)
  ) {
    return 'Fallback';
  }

  if (isHandoff) {
    if (channel === 'voice') {
      if (isVoiceOutboundEnabled) {
        return subflow_type === 'voice_outbound' ? 'Outbound' : 'Subagent';
      } else {
        return 'Subagent - Autoflows';
      }
    }

    if (isAutoflow || isClassicWorkflowsDeprecated) {
      return 'Subagent - Autoflows';
    }
    return 'Subagent - Classic';
  }

  if (isKnowledge) {
    return 'Knowledge Retrieval';
  }

  if (isClassicWorkflowsDeprecated) {
    return 'Autoflows';
  }

  if (channel === 'api') {
    return isApiAutoflow ? 'Autoflows' : 'Classic';
  }
  if (channel === 'slack') {
    return isSlackAutoflow ? 'Autoflows' : 'Classic';
  }
  if (channel === 'voice') {
    return 'Autoflows';
  }
  return isAutoflow ? 'Autoflows' : 'Classic';
};

export const getEmailWorkflowTypeDisplay = ({
  email_autopilot_enabled: emailAutopilotEnabled,
  intent_definition_id: intentDefinitionId,
  is_handoff: isHandoff,
}: IntentDataWithTopics): string => {
  const isKnowledge =
    intentDefinitionId === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE;
  const isFallback = intentDefinitionId === CommonIntentWorkflowType.FALLBACK;

  if (isFallback) {
    return 'Fallback';
  }

  if (isKnowledge) {
    return 'Knowledge Retrieval';
  }

  if (isHandoff) {
    return 'Subagent';
  }

  if (emailAutopilotEnabled) {
    return 'Intent - Autoflow';
  }

  return 'Intent';
};

/**
 * Filter value for the "Improved policy" table filter. Namespaced under
 * `policy.` so it is distinguished from `activate.`, `types.` and topic
 * filters when the active filters are parsed.
 */
export const IMPROVED_POLICY_FILTER_VALUE = 'policy.improved';

/**
 * Whether a workflow row passes the "Improved policy" filter. When the filter
 * is disabled or not active every row passes; when active, only rows with a
 * pending improved policy are kept — the same condition that renders the
 * Improved Policy badge in BadgesForWorkflow.
 */
export const matchesImprovedPolicyFilter = (
  row: Pick<IntentData, 'has_pending_improvement'>,
  filters: string[],
  isEnabled: boolean,
): boolean =>
  !isEnabled ||
  !filters.includes(IMPROVED_POLICY_FILTER_VALUE) ||
  Boolean(row.has_pending_improvement);
